/**
 * bannerRenderer — Shared pure helpers for storefront hero + admin preview.
 *
 * SATU sumber kebenaran untuk cara Dynicty me-render banner responsif:
 *   - Container geometry (aspect / height) per breakpoint.
 *   - Object-position dari focal_point (LEFT / CENTER / RIGHT).
 *   - Kebijakan mixed-slide (satu geometry per carousel supaya tidak jump).
 *
 * Aturan produk (Single Responsive Banner System):
 *   1. Tenant boleh cukup upload satu Banner Utama; kami render otomatis di
 *      desktop, tablet, dan mobile dengan controlled crop.
 *   2. Mobile Banner Khusus (mobile_image_url) OPSIONAL sebagai override
 *      lanjutan. Bila ada, mobile pakai gambar itu di container portrait 3:4.
 *   3. focal_point (default CENTER) menentukan sisi gambar yang tetap terlihat
 *      saat controlled crop pada primary banner. Tidak ada offset piksel ajaib.
 *
 * Kebijakan mixed-slide (deterministic, DOKUMENTED):
 *   - Bila SEMUA slide aktif punya mobile_image_url → carousel mobile pakai
 *     aspect-[3/4] (portrait mode).
 *   - Selain itu → landscape heights lama (responsive fallback). Ini mencegah
 *     container height "jumping" antar slide karena keputusan geometry
 *     dilakukan sekali per carousel, bukan per slide.
 */

// ==================== FOCAL POINT ====================
export const VALID_FOCAL_POINTS = ["left", "center", "right"];

/** Normalisasi input focal_point → salah satu VALID_FOCAL_POINTS (default center). */
export function normalizeFocalPoint(fp) {
  const v = (fp || "").toString().trim().toLowerCase();
  return VALID_FOCAL_POINTS.includes(v) ? v : "center";
}

/**
 * Tailwind class untuk object-position berdasarkan focal_point.
 * Menggunakan kelas literal supaya JIT scanner Tailwind menyertakan semuanya.
 */
export function getFocalObjectPosition(fp) {
  switch (normalizeFocalPoint(fp)) {
    case "left":
      return "object-left";
    case "right":
      return "object-right";
    case "center":
    default:
      return "object-center";
  }
}

// ==================== MOBILE OVERRIDE ====================
/**
 * Mobile override hanya dihitung bila mobile_image_url ADA dan BERBEDA dari
 * image_url. URL identik = admin mengunggah gambar yang sama → dianggap tanpa
 * override (single source), sehingga container portrait 3:4 tidak memotong
 * gambar landscape. Data tersimpan tidak diubah — ini murni aturan resolver.
 */
export function hasMobileOverride(b) {
  const mobile = ((b && b.mobile_image_url) || "").toString().trim();
  const primary = ((b && b.image_url) || "").toString().trim();
  return !!mobile && mobile !== primary;
}

// ==================== MIXED-SLIDE MODE ====================
/**
 * Deterministic mobile carousel mode.
 *   - "portrait":    semua slide aktif punya mobile override (lihat hasMobileOverride) → 3:4 container.
 *   - "responsive":  fallback landscape heights (primary banner + focal crop).
 */
export function resolveMobileMode(slides) {
  const list = Array.isArray(slides) ? slides : [];
  const hasAll = list.length > 0 && list.every((b) => hasMobileOverride(b));
  return hasAll ? "portrait" : "responsive";
}

// ==================== PORTRAIT SAFETY FALLBACK (GCP image-focus) ====================
/**
 * GCP = Marketplace Landscape Mode: kontainer selalu landscape (2:1, 21:9 ≥xl) dan
 * SEMUA sumber landscape / near-landscape (8:3, 21:9, 2:1, 16:9, 3:2, 4:3) di-render
 * object-cover + focal_point. Satu-satunya pengecualian: sumber benar-benar potret
 * (sourceRatio < 1.0, salah upload) → object-contain + backdrop blur agar tidak
 * di-zoom ekstrem. Ukuran belum diketahui → cover (tanpa CLS).
 */
export const GCP_PORTRAIT_FALLBACK_MAX_RATIO = 1.0;

export function isPortraitSource({ naturalWidth, naturalHeight }) {
  const w = Number(naturalWidth);
  const h = Number(naturalHeight);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return false;
  return w / h < GCP_PORTRAIT_FALLBACK_MAX_RATIO;
}

/** "cover" | "contain" — contain HANYA untuk sumber potret (fallback darurat). */
export function classifyImageFit(dims) {
  return isPortraitSource(dims || {}) ? "contain" : "cover";
}

// ==================== CONTAINER CLASSES ====================
/**
 * className container hero utama (storefront). Layout options:
 *   - "hero-full"             → full-bleed, tinggi lebih besar.
 *   - "premium-commerce"      → full-width edge-to-edge (Global Commerce Premium),
 *                               Marketplace Landscape Mode: 2:1 di SEMUA lebar (mobile,
 *                               tablet, desktop) dan 21:9 ≥xl; tanpa radius, tanpa fixed
 *                               height, tanpa max-height (max-h mengubah rasio tak terkontrol).
 *   - "banner-sections-grid"  → boxed rounded (default).
 * Kelas Tailwind ditulis literal (bukan template dinamis) agar JIT-safe.
 */
export function computeHeroContainerClass({ slides, layout }) {
  const mode = resolveMobileMode(slides);
  if (layout === "premium-commerce") {
    if (mode === "portrait") {
      return "relative overflow-hidden w-full aspect-[3/4] md:aspect-[2/1] xl:aspect-[21/9]";
    }
    return "relative overflow-hidden w-full aspect-[2/1] xl:aspect-[21/9]";
  }
  if (layout === "hero-full") {
    if (mode === "portrait") {
      return "relative overflow-hidden aspect-[3/4] sm:aspect-[3/4] md:aspect-auto md:h-[500px] -mx-4 sm:-mx-6 lg:-mx-8 xl:mx-0 xl:rounded-[var(--storefront-hero-radius)]";
    }
    return "relative overflow-hidden h-[300px] sm:h-[380px] md:h-[500px] -mx-4 sm:-mx-6 lg:-mx-8 xl:mx-0 xl:rounded-[var(--storefront-hero-radius)]";
  }
  if (mode === "portrait") {
    return "relative rounded-[var(--storefront-hero-radius)] overflow-hidden aspect-[3/4] sm:aspect-[3/4] md:aspect-auto md:h-[430px]";
  }
  return "relative rounded-[var(--storefront-hero-radius)] overflow-hidden h-[270px] sm:h-[340px] md:h-[430px]";
}

// ==================== ADMIN PREVIEW CLASSES ====================
/**
 * className container preview admin untuk satu breakpoint.
 * Preview memakai rasio yang SAMA dengan storefront live (SSOT) supaya
 * "WYSIWYG" — apa yang admin lihat = apa yang customer lihat.
 *
 *   - "desktop": landscape 16:9 (mirip storefront ≥md).
 *   - "mobile" + hasMobileOverride: portrait 3:4 (mobile override).
 *   - "mobile" + no override:       landscape 27:20 (mirip h-[270px] ×
 *     lebar viewport ~360-430 px = ratio ~1.4). Kami pakai aspect-[27/20]
 *     supaya proporsional dengan storefront responsive fallback.
 */
export function computePreviewContainerClass({ breakpoint, hasMobileOverride }) {
  if (breakpoint === "desktop") return "aspect-[16/9]";
  if (breakpoint === "mobile") {
    return hasMobileOverride ? "aspect-[3/4]" : "aspect-[27/20]";
  }
  // tablet fallback (tidak dipakai di UI v1 tapi tersedia)
  return "aspect-[16/10]";
}

/**
 * Sumber gambar yang harus dipakai untuk sebuah breakpoint preview,
 * mengikuti aturan storefront: mobile pakai override bila ada, sisanya primary.
 */
export function pickPreviewImage({ breakpoint, image_url, mobile_image_url }) {
  if (breakpoint === "mobile" && mobile_image_url) return mobile_image_url;
  return image_url || "";
}
